import { useEffect, useRef, useState } from "react";
import { t } from "@chuzi/shared/config";
import type { RealmId } from "@chuzi/shared/types";
import { api } from "../lib/api";
import {
  CATALOG_SORT_ORDERS,
  catalogSortLexiconKey,
  type CatalogSortOrder,
} from "../lib/catalog-sort";

interface TagSearchBarProps {
  realm: RealmId;
  value: string;
  onChange: (value: string) => void;
  onSelectTag?: (tag: string) => void;
  sortOrder?: CatalogSortOrder;
  onSortOrderChange?: (order: CatalogSortOrder) => void;
  showSort?: boolean;
}

export function TagSearchBar({
  realm,
  value,
  onChange,
  onSelectTag,
  sortOrder = "newest",
  onSortOrderChange,
  showSort = true,
}: TagSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120," : "rgba(126,184,255,";
  const activeColor = isWilds ? "#78c878" : "#7eb8ff";

  useEffect(() => {
    let cancelled = false;
    const q = value.trim();
    if (!q) {
      setSuggestions([]);
      return;
    }

    api.tags
      .index({ q })
      .then((res) => {
        if (!cancelled) setSuggestions(res.data);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [value]);

  function pickSuggestion(tag: string) {
    onChange(tag);
    onSelectTag?.(tag);
    setShowDropdown(false);
    inputRef.current?.focus();
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 120,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        width: "min(520px, calc(100vw - 48px))",
        pointerEvents: "auto",
      }}
    >
      {value.trim() ? (
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: 3,
            textTransform: "lowercase",
            color: activeColor,
            textShadow: `0 0 24px ${accent}0.55), 0 2px 12px rgba(0,0,0,0.8)`,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          #{value.trim().toLowerCase()}
        </div>
      ) : null}

      <div style={{ position: "relative", width: "100%" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            borderRadius: 999,
            border: `1px solid ${accent}0.35)`,
            background: "rgba(8,12,24,0.88)",
            backdropFilter: "blur(10px)",
            boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px ${accent}0.12)`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
            }}
          >
            <span style={{ color: `${accent}0.7)`, fontSize: 14, fontWeight: 700 }}>#</span>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => {
                setTimeout(() => setShowDropdown(false), 200);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && suggestions.length > 0) {
                  e.preventDefault();
                  pickSuggestion(suggestions[0]);
                }
              }}
              placeholder={t(realm, "tags_placeholder")}
              style={{
                flex: 1,
                minWidth: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "#e8f0ff",
                fontSize: 14,
                letterSpacing: 0.3,
              }}
              autoComplete="off"
              spellCheck={false}
            />
            {value.trim() ? (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  inputRef.current?.focus();
                }}
                title={t(realm, "tags_clear")}
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(232,240,255,0.45)",
                  cursor: "pointer",
                  padding: "0 2px",
                  fontSize: 16,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            ) : null}
          </div>

          {showSort ? (
            <>
              <div
                style={{
                  width: 1,
                  alignSelf: "stretch",
                  background: `${accent}0.25)`,
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 14px",
                }}
              >
                <select
                  value={sortOrder}
                  onChange={(e) => onSortOrderChange?.(e.target.value as CatalogSortOrder)}
                  aria-label={t(realm, "catalog_order_label")}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: "#e8f0ff",
                    fontSize: 13,
                    letterSpacing: 0.2,
                    cursor: "pointer",
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                >
                  {CATALOG_SORT_ORDERS.map((order) => (
                    <option
                      key={order}
                      value={order}
                      style={{ background: "#080c18", color: "#e8f0ff" }}
                    >
                      {t(realm, catalogSortLexiconKey(order))}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}
        </div>

        {showDropdown && suggestions.length > 0 ? (
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              width: "50%",
              borderRadius: 12,
              border: `1px solid ${accent}0.28)`,
              background: "rgba(8,12,24,0.96)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
            }}
          >
            {suggestions.map((tag) => (
              <button
                key={tag}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(tag)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  color: "#e8f0ff",
                  padding: "10px 14px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                #{tag}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
